/**
 * Closeout "Try who has the ball" — the per-person switch and the shapes the
 * queue sends (threads/who-has-the-ball; mockup
 * https://claude.ai/artifact/6yPpNbU7So9a27GDhpwojF).
 *
 * Same server-backed store as "Try the new Grid" (`user_preferences`,
 * /api/me/preferences), so it follows a person from desk to laptop. Default
 * OFF: until someone flips it for themselves Closeout is exactly as it was.
 * The server reads the same key to decide whether approving a quote closes
 * the quote tasks people made (closeoutQuoteTasks.ts).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const BALL_PREF_KEY = "closeout.ball";
/** Settings → Organization "Who has the ball — on for everyone" (missing = on). */
export const BALL_FOR_EVERYONE_KEY = "closeout.ball_for_everyone";
export const BALL_SWITCH_QUERY_KEY = ["close-out-ball-switch"] as const;

export interface BallSwitchState {
    on: boolean;
    forEveryone: boolean;
    mine: boolean | null;
    /** "Try whose turn" (715): its own per-person switch, off by default. */
    whoseTurn?: boolean;
}

/**
 * The switch as this person sees it: the org's "on for everyone" (10/16) plus
 * their own choice. With the org on, the personal switch is an opt-out. The
 * server works it out (GET /api/close-out/ball-switch) so the screen and the
 * server-side behaviour (closing quote tasks, task order) always agree. Fails
 * safe to OFF: if it can't be read, Closeout looks exactly as it used to.
 */
export function useBallSwitch(enabled = true): {
    on: boolean;
    forEveryone: boolean;
    set: (on: boolean) => void;
    loaded: boolean;
} {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: BALL_SWITCH_QUERY_KEY,
        queryFn: () => api.get<BallSwitchState>("/api/close-out/ball-switch"),
        staleTime: 5 * 60 * 1000,
        retry: false,
        enabled,
    });
    const set = (next: boolean) => {
        const prev = q.data;
        if (prev) qc.setQueryData(BALL_SWITCH_QUERY_KEY, { ...prev, on: next, mine: next });
        api.put(`/api/me/preferences/${encodeURIComponent(BALL_PREF_KEY)}`, { value: next })
            .then(() => qc.invalidateQueries({ queryKey: BALL_SWITCH_QUERY_KEY }))
            .catch(() => {
                if (prev) qc.setQueryData(BALL_SWITCH_QUERY_KEY, prev);
                toast.error("Couldn't save the switch. It's back the way it was.");
            });
    };
    return {
        on: q.data?.on === true,
        forEveryone: q.data?.forEveryone === true,
        set,
        loaded: q.isFetched || q.isError,
    };
}

/** "Try whose turn" (Closeout "Whose turn is it?", 10/28): same store. */
export const WHOSE_TURN_PREF_KEY = "closeout.whose_turn";

/**
 * "Try whose turn" for this person. Only does anything while who has the
 * ball is on. Off until they flip it (Darcy pilots first). Fails safe to off.
 */
export function useWhoseTurn(enabled = true): { on: boolean; set: (on: boolean) => void; loaded: boolean } {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: BALL_SWITCH_QUERY_KEY,
        queryFn: () => api.get<BallSwitchState>("/api/close-out/ball-switch"),
        staleTime: 5 * 60 * 1000,
        retry: false,
        enabled,
    });
    const set = (next: boolean) => {
        const prev = q.data;
        if (prev) qc.setQueryData(BALL_SWITCH_QUERY_KEY, { ...prev, whoseTurn: next });
        api.put(`/api/me/preferences/${encodeURIComponent(WHOSE_TURN_PREF_KEY)}`, { value: next })
            .then(() => qc.invalidateQueries({ queryKey: BALL_SWITCH_QUERY_KEY }))
            .catch(() => {
                if (prev) qc.setQueryData(BALL_SWITCH_QUERY_KEY, prev);
                toast.error("Couldn't save the switch. It's back the way it was.");
            });
    };
    return { on: q.data?.on === true && q.data?.whoseTurn === true, set, loaded: q.isFetched || q.isError };
}

/** Where a job sits from one person's seat (server: closeoutWhoseTurn.ts). */
export type TurnLane = "came_back" | "on_you" | "pile" | "stuck" | "waiting";

/** Waiting on someone outside past its limit, and the one nudge. */
export interface TurnStuck {
    kind: string;
    line: string;
    since: string;
    action: { label: string; kind: "open" | "hand_to_approver"; station: number };
}

export interface Turn {
    lane: TurnLane;
    /** The face: who has it now (first name); null for the pile / outside. */
    who: string | null;
    whoUserId: string | null;
    line: string;
    /** When it became this person's turn (the one clock). */
    since: string | null;
    workDays: number;
    /** On you and more than 2 work days: the soft amber dot. */
    amber: boolean;
    /** Handed to you and a full work day old: goes to the top. */
    rise: boolean;
    stepKey: string | null;
    claimedBy: { userId: string; name: string } | null;
    stuck?: TurnStuck | null;
}

/** "since 2:14 PM" today, "since Tue" this week, else "since Oct 14". */
export function sinceLabel(iso: string | null | undefined, now: Date = new Date()): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const tz = "America/Chicago";
    const day = (x: Date) => x.toLocaleDateString("en-US", { timeZone: tz });
    if (day(d) === day(now)) {
        return `since ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz })}`;
    }
    if (now.getTime() - d.getTime() < 6 * 24 * 3600 * 1000) {
        return `since ${d.toLocaleDateString("en-US", { weekday: "short", timeZone: tz })}`;
    }
    return `since ${d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: tz })}`;
}

/** A step a job can be handed over for (server: closeoutAsks.ts). */
export interface AskOption {
    kind: string;
    label: string;
}

export type BallKind = "office" | "approver" | "city" | "customer" | "scheduling" | "tech";
export type BallTopic =
    | "quote"
    | "reports"
    | "filing"
    | "invoice"
    | "payment"
    | "packet"
    | "return_trip"
    /** "No work order yet" (2026-10-21): the office uploads it. */
    | "work_order";

/** One holder per job, worded for the viewer (server: closeoutBall.ts). */
export interface Ball {
    kind: BallKind;
    topic: BallTopic;
    who: string;
    userId: string | null;
    text: string;
    mine: boolean;
    /** The job has a step only the approver can do (optional: older servers). */
    needsApprover?: boolean;
    /** One tap finishes it (send packet / quote, email or finalize invoice, charge card). */
    ready?: boolean;
    /** Nobody has opened the job since it landed with this holder. */
    unseen?: boolean;
    since: string | null;
    workDays: number;
    stale: boolean;
    waits: string[];
    note: string | null;
    noteAt: string | null;
    /** Someone handed this job to a person (Hand to…). Open, or answered and
     *  not yet seen by the sender. */
    handoff?: BallHandoff;
}

/** A hand-off (server: closeoutHandoffs.ts). */
export interface BallHandoff {
    id: string;
    state: "open" | "answered";
    fromUserId: string | null;
    fromName: string;
    toUserId: string;
    toName: string;
    reason: string;
    at: string;
    outcome: "done" | "handed_back" | null;
    reply: string | null;
    replyByName: string | null;
    replyAt: string | null;
    /** Whose turn: the step that was asked for; null = free text. */
    askKind?: string | null;
    /** GWC closed it because the step cleared. */
    autoClosed?: boolean;
    /** "Corbin approved the quote". */
    doneLine?: string | null;
}

/** A person a job can be handed to. */
export interface HandoffPerson {
    id: string;
    name: string;
    first_name: string;
    role: string;
}

/** Hand-off colours: pink while it is with someone, green when it came back. */
export const HANDOFF_COLOR = "#be185d";
export const HANDOFF_REPLY_COLOR = "#15803d";

/** "2:14 PM" today, else "Tue 2:14 PM" (office time zone). */
export function shortWhen(iso: string | null | undefined, now: Date = new Date()): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const tz = "America/Chicago";
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: tz });
    const day = (x: Date) => x.toLocaleDateString("en-US", { timeZone: tz });
    if (day(d) === day(now)) return time;
    return `${d.toLocaleDateString("en-US", { weekday: "short", timeZone: tz })} ${time}`;
}

/**
 * The line a hand-off comes back with, for everyone: "Ready for Darcy:
 * Corbin approved the quote, 2:14 PM". A typed answer keeps its words.
 */
export function handoffReturnLine(h: BallHandoff, now: Date = new Date()): string {
    const when = shortWhen(h.replyAt, now);
    if (!(h.reply ?? "").trim() && h.outcome === "done" && h.doneLine) {
        return `Ready for ${h.fromName}: ${h.doneLine}${when ? `, ${when}` : ""}`;
    }
    return handoffReplyLine(h);
}

/** "Corbin: yes, make them Net 30" / "Corbin: done". */
export function handoffReplyLine(h: BallHandoff): string {
    const who = h.replyByName || h.toName;
    const said = (h.reply ?? "").trim();
    return `${who}: ${said || (h.outcome === "handed_back" ? "handed it back" : "done")}`;
}

/**
 * A job note (server: closeoutJobNotes.ts). Shared with the whole office
 * again (10/16) with the writer's name and date; "only me" is optional.
 * A note never moves the ball — Hand to… does that.
 */
export interface JobNote {
    id: string;
    jobId: string;
    authorId: string | null;
    authorName: string;
    text: string;
    onlyMe: boolean;
    at: string;
    editedByName: string | null;
    canEdit: boolean;
}

/** "Oct 14" (office time zone). */
export function noteDate(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Chicago" });
}

/** "Darcy · Oct 14: waiting on PO from Cintas". */
export function noteLine(n: Pick<JobNote, "authorName" | "at" | "text">): string {
    const when = noteDate(n.at);
    return `${n.authorName}${when ? ` · ${when}` : ""}: ${n.text.replace(/\s+/g, " ").trim()}`;
}

/** The newest note everyone in the office can see (not an "only me" one). */
export function latestSharedNote(notes: JobNote[] | null | undefined): JobNote | null {
    return (notes ?? []).find((n) => !n.onlyMe) ?? null;
}

/**
 * "Why isn't this out yet?" in one line: the specific blocker (the ball),
 * the next thing it waits on, and the newest shared note. Null once the
 * packet has gone out, or when there is nothing to say.
 */
export function whyNotOut(job: {
    ball?: Ball | null;
    notes?: JobNote[] | null;
    packet_sent_at?: string | null;
}): { blocker: string | null; also: string | null; note: string | null } | null {
    if (job.packet_sent_at) return null;
    const blocker = job.ball?.text ?? null;
    const also = job.ball?.waits?.[0] ?? null;
    const n = latestSharedNote(job.notes);
    const note = n ? noteLine(n) : null;
    if (!blocker && !note) return null;
    return { blocker, also, note };
}

/** The same thing as one sentence (tooltips, screen readers). */
export function whyNotOutText(why: NonNullable<ReturnType<typeof whyNotOut>>): string {
    return [why.blocker, why.also, why.note ? `Note: ${why.note}` : null].filter(Boolean).join(". ");
}

/** Dot / text colour per holder (the mockup's legend). */
export const BALL_COLOR: Record<BallKind, string> = {
    office: "#0f766e",
    approver: "#7c3aed",
    city: "#2563eb",
    customer: "#c2410c",
    scheduling: "#8a5a1c",
    tech: "#475569",
};
export const BALL_AMBER = "#f59e0b";

/** "today", "1d", "4d". */
export function daysLabel(n: number): string {
    return n <= 0 ? "today" : `${n}d`;
}

/** Which Closeout step a topic is worked on. */
export function stationForTopic(topic: BallTopic): number {
    switch (topic) {
        case "quote":
            return 3;
        case "invoice":
            return 2;
        case "payment":
            return 4;
        case "packet":
            return 5;
        default:
            return 1;
    }
}

/** The step in plain words with a capital: "Send the quote". Only for the
 *  viewer's own ball; someone else's is "Open it in Closeout". */
export function primaryActionLabel(ball: Ball): string {
    if (!ball.mine) return "Open it in Closeout";
    const action = ball.text.replace(/^(Yours|Locked|Held) — /, "");
    return action.charAt(0).toUpperCase() + action.slice(1);
}

const TOPIC_WORD: Record<BallTopic, string> = {
    quote: "quote",
    reports: "job's reports",
    filing: "city filing",
    invoice: "invoice",
    payment: "payment",
    packet: "packet",
    return_trip: "return trip",
    work_order: "work order",
};

/** "today at 4:26 PM" / "on Oct 9 at 4:26 PM". */
export function whenLabel(iso: string | null, now: Date = new Date()): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return `today at ${time}`;
    return `on ${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} at ${time}`;
}

/** The soft stop's sentence: "Closeout already has this quote. Corbin
 *  approved it today at 4:26 PM." */
export function softStopLine(ball: Ball, now: Date = new Date()): string {
    const head = `Closeout already has this ${TOPIC_WORD[ball.topic]}.`;
    if (ball.note) {
        const when = whenLabel(ball.noteAt, now);
        return `${head} ${ball.note}${when ? ` ${when}` : ""}.`;
    }
    return `${head} ${ball.text}.`;
}
